import { useTranslation } from "@redotech/redo-i18n";
import type { LocationId } from "@redotech/redo-inventory-id";
import { RedoInventoryRpcClientContext } from "@redotech/redo-merchant-app-common/inventory-rpc-client-provider";
import type { StorageLocationId } from "@redotech/redo-warehouse-id";
import type { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import {
  RedoDropdownInputSize,
  RedoSingleSelectDropdownInput,
} from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { memo, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";

export interface BinOption {
  storageLocationId: StorageLocationId;
  binName: string;
}

const NO_STORAGE_LOCATION_IDS: StorageLocationId[] = [];

export const BinSearchDropdown = memo(function BinSearchDropdown({
  locationId,
  selectedBin,
  onSelect,
  includeReceivingBins,
  disabledStorageLocationIds = NO_STORAGE_LOCATION_IDS,
  disabledReason,
  placeholder,
  size = "sm",
}: {
  locationId: LocationId | undefined;
  selectedBin: BinOption | null;
  onSelect: (bin: BinOption) => void;
  includeReceivingBins: boolean;
  disabledStorageLocationIds?: StorageLocationId[];
  disabledReason?: string;
  placeholder?: string;
  size?: RedoDropdownInputSize;
}) {
  const { t } = useTranslation();
  const [searchString, setSearchString] = useState("");
  const debouncedSearch = useDebounce(searchString, 200);

  const { data: binsData, isLoading: binsLoading } = useRpcQuery(
    RedoInventoryRpcClientContext,
    "searchBins",
    {
      locationId,
      search: debouncedSearch.trim(),
      includeStorageBins: true,
      includeReceivingBins,
    },
    { enabled: !!locationId },
  );

  const options: TextRedoListItem<BinOption>[] = useMemo(
    () =>
      (binsData?.bins ?? []).map((bin) => ({
        type: "text" as const,
        id: bin.storageLocationId,
        value: {
          storageLocationId: bin.storageLocationId,
          binName: bin.binName,
        },
        text: bin.binName,
        supportingText: disabledStorageLocationIds.includes(
          bin.storageLocationId,
        )
          ? disabledReason
          : undefined,
      })),
    [binsData?.bins, disabledStorageLocationIds, disabledReason],
  );

  const selectedOption: TextRedoListItem<BinOption> | undefined = useMemo(
    () =>
      selectedBin
        ? (options.find((o) => o.id === selectedBin.storageLocationId) ?? {
            type: "text",
            id: selectedBin.storageLocationId,
            value: selectedBin,
            text: selectedBin.binName,
          })
        : undefined,
    [options, selectedBin],
  );

  return (
    <RedoSingleSelectDropdownInput
      disabledItems={disabledStorageLocationIds}
      itemsLoading={binsLoading}
      options={options}
      optionSelected={({ item }) => {
        setSearchString("");
        onSelect(item.value);
      }}
      placeholder={placeholder ?? t("Select a bin")}
      searchPlaceholder={t("Search bins")}
      searchString={searchString}
      selectedItem={selectedOption}
      setSearchString={setSearchString}
      size={size}
    />
  );
});
